import { Box, Flex, Grid } from '@/components/atoms/Grid'
import { FC } from 'react'
import styled from 'styled-components'
import { transparentize } from 'polished'
import { Body2 } from '@/components/atoms/typography'
import { PromoCode } from '@/network/graphql.g'
import Trans from 'next-translate/Trans'
import { validFor } from '@/theme/validFor'
import ProductTimeCounter from '@/components/molecules/productTimeCounter'
import { useTodayCountdown } from '../../../hooks/useDailyCountdown'

type ProductDetailCouponNewProps = {
  promoCodes: PromoCode | PromoCode[]
  modalMode?: boolean
}

const ProductDetailCouponNew: FC<ProductDetailCouponNewProps> = ({
  promoCodes,
  modalMode
}) => {
  const { startTime, endTime } = useTodayCountdown()

  const codes = Array.isArray(promoCodes) ? promoCodes : [promoCodes]

  return (
    <Wrapper>
      <Flex justifyContent="flex-end" mt={15}>
        <StyledCouponCountdownWrapper>
          <ProductTimeCounter startTime={startTime} endTime={endTime} />
        </StyledCouponCountdownWrapper>
      </Flex>
      <StockDiscountWrapper modalMode={modalMode}>
        {codes.length > 0 && (
          <Flex flexDirection="column" width="100%" gap="8px">
            {codes.map((promoCode, index) => (
              <DiscountItem key={promoCode.id || index}>
                <ItemRow modalMode={modalMode}>
                  <StockDiscountAmount modalMode={modalMode}>
                    {`-${promoCode.discountValue}%`}
                  </StockDiscountAmount>
                  <DiscountTextWrapper modalMode={modalMode}>
                    <Trans
                      i18nKey="common:ProductDetail.Promo.buyWithCouponNew"
                      components={[<DiscountText modalMode={modalMode} />]}
                      values={{
                        stockDiscountValue: `-${promoCode.discountValue}%`,
                        stockDiscountCode: promoCode.code
                      }}
                    />
                  </DiscountTextWrapper>
                </ItemRow>
              </DiscountItem>
            ))}
          </Flex>
        )}
      </StockDiscountWrapper>
    </Wrapper>
  )
}

export default ProductDetailCouponNew

const Wrapper = styled(Box)`
  margin-top: 10px;
`

const StockDiscountWrapper = styled(Flex)`
  padding: ${(props) => (!props.modalMode ? '10px 12px' : '10px 6px')};
  border-radius: 6px 0 6px 6px;
  flex-direction: column;
  background-color: ${(props) =>
    transparentize(0.8, props.theme.colors.stockDiscountColor)};
  ${validFor.mobile`
   padding: 10px 6px;
  `}
`

const StockDiscountAmount = styled(Body2)`
  font-size: ${(props) => (!props.modalMode ? '16px' : '14px')};
  background-color: ${(props) => props.theme.colors.background};
  color: ${(props) => props.theme.colors.accent};
  padding: ${(props) => (!props.modalMode ? '5px 7px' : '4px 3px')};
  border: 1px solid ${(props) => props.theme.colors.accent};
  border-radius: 6px;
  margin: 0 0 4px 0;
  align-self: center;
  white-space: nowrap;
  display: inline-block;
  width: 40px;
  text-align: center;
  flex: 0 0 auto;
  ${validFor.mobile`
   padding: 3px 5px;
   font-size: 14px;
    width: 34px;
  `}
`

const DiscountItem = styled(Flex)`
  align-items: flex-start;
  justify-content: flex-start;
  border-radius: 6px;
  padding: 2px;
`

const ItemRow = styled(Flex)`
  align-items: center;
  gap: ${(props) => (!props.modalMode ? '8px' : '2px')};
`

const DiscountTextWrapper = styled(Flex)`
  font-size: ${(props) => (!props.modalMode ? '14px' : '12px')};
  flex-basis: ${(props) => (!props.modalMode ? '1 1 260px' : '1 1 220px')};
  align-items: center;
  min-width: 0;
  flex-wrap: wrap;
  margin-left: ${(props) => (!props.modalMode ? '8px' : '4px')};
  ${validFor.tablet`
    font-size: 12px;
  `}
  ${validFor.mobile`
    font-size: 12px;
    margin-left: 4px;
  `}
`

const DiscountText = styled.span`
  color: ${(props) => props.theme.colors.onBackground};
  text-transform: uppercase;
  font-weight: bold;
  padding: 0 4px;
  font-size: ${(props) => (!props.modalMode ? '14px' : '12px')};
`

const StyledCouponCountdownWrapper = styled(Grid)`
  background-color: ${(props) => props.theme.colors.accent};
  color: ${(props) => props.theme.colors.background};
  grid-template-columns: auto;
  grid-template-rows: auto;
  align-items: center;
  grid-gap: 10px;
  width: 130px;
  padding-top: 1px;
  margin-bottom: -1px;
`
